// Client-only — all functions guard against SSR via typeof window checks.
import type { User } from '@/shared/types'

const NS = 'diecastshowcase'

const KEYS = {
  ACCESS_TOKEN:  `${NS}_access_token`,
  REFRESH_TOKEN: `${NS}_refresh_token`,
  USER:          `${NS}_user`,
} as const

// ─────────────────────────────────────────────────────────────────────────────
// TOKEN HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(KEYS.ACCESS_TOKEN)
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(KEYS.REFRESH_TOKEN)
}

export function setTokens(accessToken: string, refreshToken: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(KEYS.ACCESS_TOKEN, accessToken)
  localStorage.setItem(KEYS.REFRESH_TOKEN, refreshToken)
  dispatchAuthChange()
}

export function clearTokens(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(KEYS.ACCESS_TOKEN)
  localStorage.removeItem(KEYS.REFRESH_TOKEN)
}

// ─────────────────────────────────────────────────────────────────────────────
// USER HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function getUser(): User | null {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(KEYS.USER)
  if (!raw) return null
  try {
    return JSON.parse(raw) as User
  } catch {
    localStorage.removeItem(KEYS.USER)
    return null
  }
}

export function setUser(user: User): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(KEYS.USER, JSON.stringify(user))
  dispatchAuthChange()
}

export function clearUser(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(KEYS.USER)
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTH STATE
// ─────────────────────────────────────────────────────────────────────────────

export function isAuthenticated(): boolean {
  return getAccessToken() !== null && getUser() !== null
}

/** Store tokens + user after a successful login / signup / Google auth */
export function persistAuth(accessToken: string, refreshToken: string, user: User): void {
  setTokens(accessToken, refreshToken)
  setUser(user)
}

/** Wipe all auth state and notify listeners */
export function clearAuth(): void {
  clearTokens()
  clearUser()
  dispatchAuthChange()
}

// ─────────────────────────────────────────────────────────────────────────────
// EVENT BUS  (cross-component auth state sync)
// ─────────────────────────────────────────────────────────────────────────────

export const AUTH_CHANGE_EVENT = 'diecastshowcase:authChanged'

function dispatchAuthChange(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT))
} 